import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import MarketRoomPage from "../arena/MarketRoomPage";
import TeamPage from "../arena/TeamPage";
import MindPage from "../arena/MindPage";
import OracleDeck from "../arena/OracleDeck";
import ResearchSection from "../research/ResearchSection";
import "./aeon-console-preview.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const API_KEY = process.env.REACT_APP_API_KEY || "";
const AXIOS_CONFIG = { headers: { "X-API-Key": API_KEY } };

const NAV_ITEMS = [
  { id: "hub", code: "01", label: "HUB" },
  { id: "trading", code: "02", label: "TRADING" },
  { id: "room", code: "03", label: "ROOM" },
  { id: "strategies", code: "04", label: "STRATEGIES" },
  { id: "desk", code: "05", label: "DESK" },
  { id: "brain", code: "06", label: "BRAIN" },
  { id: "outlook", code: "07", label: "OUTLOOK" },
  { id: "research", code: "08", label: "RESEARCH" },
];

const EMPTY = "—";

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const parsed = number(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

const money = (value, { signed = false } = {}) => {
  const parsed = number(value);
  if (parsed === null) return EMPTY;
  const sign = signed && parsed > 0 ? "+" : "";
  return `${sign}${parsed.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const percent = (value, { ratio = false, signed = false } = {}) => {
  const parsed = number(value);
  if (parsed === null) return EMPTY;
  const normalized = ratio ? parsed * 100 : parsed;
  const sign = signed && normalized > 0 ? "+" : "";
  return `${sign}${normalized.toFixed(1)}%`;
};

const compactNumber = (value) => {
  const parsed = number(value);
  if (parsed === null) return EMPTY;
  return parsed.toLocaleString(undefined, {
    notation: Math.abs(parsed) >= 10000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(parsed) < 10 ? 4 : 2,
  });
};

const cleanSymbol = (value) => String(value || EMPTY)
  .replace("/USDT", "")
  .replace("-USDT", "")
  .replace("USDT", "");

const statusTone = (value) => {
  const state = String(value || "").toLowerCase();
  if (["healthy", "active", "operational", "live", "connected", "promote"].some((word) => state.includes(word))) return "good";
  if (["critical", "halt", "error", "failed", "disable", "restricted"].some((word) => state.includes(word))) return "bad";
  if (["warn", "degraded", "sandbox", "cool", "monitor"].some((word) => state.includes(word))) return "warn";
  return "muted";
};

const asArray = (value) => Array.isArray(value) ? value : [];

const normalizePositions = (payload) => {
  if (Array.isArray(payload)) return payload;
  return asArray(payload?.positions || payload?.open_trades || payload?.data);
};

const getPositionSide = (position) =>
  String(position.direction || position.side || position.position_side || EMPTY).toUpperCase();

const positionPnl = (position) =>
  firstNumber(position.unrealized_pnl, position.pnl_usd, position.current_pnl, position.pnl);

const positionEntry = (position) =>
  firstNumber(position.entry_price, position.entry, position.avg_entry_price);

const positionMark = (position) =>
  firstNumber(position.current_price, position.mark_price, position.price);

const positionSize = (position) =>
  firstNumber(position.notional, position.position_value, position.size_usd, position.quantity, position.size);

const positionEngine = (position) =>
  position.strategy || position.engine || position.source || EMPTY;

const marketChange = (row) =>
  firstNumber(row?.change_24h, row?.change, row?.pct_change, row?.price_change_percent);

function DataState({ children, available = true }) {
  return available ? children : <span className="console-na">NOT AVAILABLE</span>;
}

function Metric({ label, value, detail, tone = "neutral" }) {
  return (
    <div className="console-metric">
      <span>{label}</span>
      <strong className={`tone-${tone}`}>{value}</strong>
      <small>{detail || "\u00a0"}</small>
    </div>
  );
}

function SectionTitle({ children, detail, action }) {
  return (
    <div className="console-section-title">
      <div>
        <strong>{children}</strong>
        {detail ? <span>{detail}</span> : null}
      </div>
      {action}
    </div>
  );
}

function EmptyPanel({ title, detail }) {
  return (
    <div className="console-empty">
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

export default function AeonConsolePreview({
  stats,
  botStatus,
  tradingStats,
  marketData,
  messages,
  operatorSummary,
  operatorWarnings,
  portfolioSummary,
  governanceSummary,
  systemHealth,
  fwSummary,
  openPositions,
  btcPrice,
  wsConnected,
  refreshing,
  actions = {},
  researcherData,
  onExit,
}) {
  const [activeView, setActiveView] = useState("hub");
  const [positions, setPositions] = useState([]);
  const [positionsReady, setPositionsReady] = useState(false);
  const [positionsError, setPositionsError] = useState("");
  const [engines, setEngines] = useState(null);
  const [clock, setClock] = useState(() => new Date());
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [briefingIndex, setBriefingIndex] = useState(0);

  const loadConsoleData = useCallback(async () => {
    const [positionResult, engineResult] = await Promise.allSettled([
      axios.get(`${API}/paper/positions`, AXIOS_CONFIG),
      axios.get(`${API}/engines/status`, AXIOS_CONFIG),
    ]);

    if (positionResult.status === "fulfilled") {
      setPositions(normalizePositions(positionResult.value.data));
      setPositionsError("");
    } else {
      setPositionsError("Paper positions endpoint did not respond.");
    }
    setPositionsReady(true);

    if (engineResult.status === "fulfilled") setEngines(engineResult.value.data);
  }, []);

  useEffect(() => {
    loadConsoleData();
    const dataTimer = window.setInterval(loadConsoleData, 8000);
    const clockTimer = window.setInterval(() => setClock(new Date()), 1000);
    return () => {
      window.clearInterval(dataTimer);
      window.clearInterval(clockTimer);
    };
  }, [loadConsoleData]);

  const marketRows = useMemo(() => {
    const symbols = asArray(marketData?.symbols || marketData?.data || marketData);
    return symbols.slice(0, 16);
  }, [marketData]);

  const positionStats = useMemo(() => {
    const unrealized = positions.reduce((sum, position) => sum + (positionPnl(position) || 0), 0);
    const notional = positions.reduce((sum, position) => sum + (positionSize(position) || 0), 0);
    const longs = positions.filter((position) => getPositionSide(position).includes("LONG")).length;
    const shorts = positions.filter((position) => getPositionSide(position).includes("SHORT")).length;
    return { unrealized, notional, longs, shorts };
  }, [positions]);

  const portfolioHeatRaw = firstNumber(
    operatorSummary?.portfolioHeat,
    portfolioSummary?.aggregate_portfolio_heat,
  );
  const portfolioHeatRatio = portfolioHeatRaw === null
    ? null
    : portfolioHeatRaw > 1 ? portfolioHeatRaw / 100 : portfolioHeatRaw;

  const serviceRows = useMemo(() => {
    const services = systemHealth?.services;
    if (Array.isArray(services)) return services;
    if (services && typeof services === "object") {
      return Object.entries(services).map(([name, value]) => ({ name, ...value }));
    }
    return [];
  }, [systemHealth]);

  const engineRows = useMemo(() => {
    const source = engines?.engines || engines?.data || engines;
    if (Array.isArray(source)) return source;
    if (source && typeof source === "object") {
      return Object.entries(source).map(([name, value]) => ({
        name,
        ...(value && typeof value === "object" ? value : { status: value }),
      }));
    }
    return [];
  }, [engines]);

  const governanceRows = asArray(governanceSummary?.states);
  const accountRows = asArray(portfolioSummary?.accounts_list);
  const messageRows = asArray(messages).slice(0, 20);
  const todayPnl = firstNumber(
    tradingStats?.today_pnl,
    portfolioSummary?.today_pnl,
  );
  const winRate = firstNumber(tradingStats?.win_rate);
  const totalTrades = firstNumber(tradingStats?.total_trades);
  const activePositionCount = positionsReady ? positions.length : firstNumber(openPositions);
  const currentBtc = firstNumber(
    btcPrice,
    marketRows.find((row) => ["BTC/USDT", "BTCUSDT"].includes(row.symbol))?.price,
  );
  const overallStatus = systemHealth?.overall || botStatus?.status || "unknown";
  const displayDate = clock.toLocaleDateString([], { month: "short", day: "2-digit", year: "numeric" });
  const displayTime = clock.toLocaleTimeString([], { hour12: false });

  const briefingSlides = useMemo(() => {
    const movers = marketRows
      .map((row) => ({ ...row, parsedChange: marketChange(row) }))
      .filter((row) => row.parsedChange !== null);
    const ascending = [...movers].sort((a, b) => a.parsedChange - b.parsedChange);
    const topLong = ascending.at(-1);
    const topShort = ascending[0];
    const topMover = [...movers].sort((a, b) => Math.abs(b.parsedChange) - Math.abs(a.parsedChange))[0];
    const advancing = movers.filter((row) => row.parsedChange > 0).length;
    const declining = movers.filter((row) => row.parsedChange < 0).length;
    const worstPosition = [...positions]
      .map((position) => ({ position, pnl: positionPnl(position) }))
      .filter((item) => item.pnl !== null)
      .sort((a, b) => a.pnl - b.pnl)[0];
    const regime = stats?.regime || stats?.market_regime;

    return [
      {
        kicker: "MARKET OVERVIEW",
        title: regime
          ? `${String(regime).replaceAll("_", " ")} · ${advancing} advancing / ${declining} declining`
          : `${advancing} advancing / ${declining} declining`,
        body: currentBtc === null
          ? "Waiting for the current market snapshot."
          : `BTC is ${money(currentBtc)}. This is the latest state reported by AEON, not a simulated tape.`,
        tone: "accent",
      },
      {
        kicker: "TOP OPPORTUNITY WATCH",
        title: topMover
          ? `${cleanSymbol(topMover.symbol)} · ${percent(topMover.parsedChange, { signed: true })}`
          : "Waiting for a ranked market",
        body: topMover
          ? "Largest absolute move in the current market snapshot. It remains a watch candidate until an engine supplies confirmation."
          : "The market endpoint has not returned enough change data to rank opportunities.",
        tone: topMover?.parsedChange >= 0 ? "good" : "bad",
      },
      {
        kicker: "STRONGEST LONG WATCH",
        title: topLong && topLong.parsedChange > 0
          ? `${cleanSymbol(topLong.symbol)} · ${percent(topLong.parsedChange, { signed: true })}`
          : "No positive leader reported",
        body: topLong && topLong.parsedChange > 0
          ? `${compactNumber(topLong.price)} current mark · strongest positive change in the tracked set.`
          : "AEON has not reported a positive market leader in this snapshot.",
        tone: "good",
      },
      {
        kicker: "STRONGEST SHORT WATCH",
        title: topShort && topShort.parsedChange < 0
          ? `${cleanSymbol(topShort.symbol)} · ${percent(topShort.parsedChange, { signed: true })}`
          : "No negative leader reported",
        body: topShort && topShort.parsedChange < 0
          ? `${compactNumber(topShort.price)} current mark · strongest negative change in the tracked set.`
          : "AEON has not reported a negative market leader in this snapshot.",
        tone: "bad",
      },
      {
        kicker: "RISK WATCH",
        title: portfolioHeatRatio === null
          ? "Portfolio heat unavailable"
          : `${percent(portfolioHeatRatio, { ratio: true })} heat · ${activePositionCount ?? EMPTY} open`,
        body: worstPosition
          ? `${cleanSymbol(worstPosition.position.symbol || worstPosition.position.pair)} is the largest current unrealized drag at ${money(worstPosition.pnl, { signed: true })}.`
          : "No losing open position was reported by the paper book.",
        tone: portfolioHeatRatio > 0.65 ? "bad" : portfolioHeatRatio > 0.4 ? "warn" : "good",
      },
    ];
  }, [activePositionCount, currentBtc, marketRows, portfolioHeatRatio, positions, stats]);

  useEffect(() => {
    const timer = window.setInterval(
      () => setBriefingIndex((index) => (index + 1) % briefingSlides.length),
      5600,
    );
    return () => window.clearInterval(timer);
  }, [briefingSlides.length]);

  const refreshAll = async () => {
    await Promise.allSettled([
      Promise.resolve(actions.refresh?.()),
      loadConsoleData(),
    ]);
  };

  return (
    <div className="aeon-console">
      <header className="console-header">
        <div className="console-brand">
          <span className="console-logo">Æ</span>
          <div>
            <strong>AEON</strong>
            <small>LOCAL DATA CONSOLE</small>
          </div>
          <span className="console-preview-badge">DESIGN PREVIEW</span>
        </div>

        <div className="console-mode-strip" aria-label="Preview data status">
          <span className="is-active"><i />REAL DATA</span>
          <span><i />NO SIMULATION</span>
          <span><i />READ-FIRST</span>
        </div>

        <div className="console-header-status">
          <button type="button" onClick={refreshAll} disabled={refreshing}>
            {refreshing ? "REFRESHING" : "REFRESH"}
          </button>
          <span className={`console-live ${wsConnected ? "is-live" : ""}`}><i />{wsConnected ? "SOCKET LIVE" : "SOCKET OFFLINE"}</span>
          <time>{displayTime}</time>
          <button type="button" className="console-exit" onClick={onExit}>RETURN LOCAL</button>
        </div>
      </header>

      <div className="console-shell">
        <nav className="console-nav">
          {NAV_ITEMS.map((item) => (
            <button
              type="button"
              key={item.id}
              className={activeView === item.id ? "is-active" : ""}
              onClick={() => setActiveView(item.id)}
            >
              <span>{item.code}</span>
              <strong>{item.label}</strong>
              <small>{item.id === "trading" ? activePositionCount ?? EMPTY : ""}</small>
            </button>
          ))}
        </nav>

        <main className="console-workspace">
          {activeView === "hub" ? (
            <section className="console-full-section console-hub">
              <SectionTitle detail="CURRENT AEON STATE · REAL SOURCES ONLY">THE HUB</SectionTitle>
              <div className="console-hub-hero">
                <div>
                  <span className="console-kicker">OPERATOR OVERVIEW · AUTO BRIEF</span>
                  <div className="console-briefing-copy" key={briefingIndex}>
                    <small>{briefingSlides[briefingIndex].kicker}</small>
                    <h1 className={`tone-${briefingSlides[briefingIndex].tone}`}>{briefingSlides[briefingIndex].title}</h1>
                    <p>{briefingSlides[briefingIndex].body}</p>
                  </div>
                  <div className="console-briefing-dots" aria-label="Operator briefing slides">
                    {briefingSlides.map((slide, index) => (
                      <button
                        type="button"
                        key={slide.kicker}
                        className={index === briefingIndex ? "is-active" : ""}
                        onClick={() => setBriefingIndex(index)}
                        title={slide.kicker}
                        aria-label={slide.kicker}
                      />
                    ))}
                  </div>
                  <div className="console-hub-actions">
                    <button type="button" onClick={() => setActiveView("trading")}>OPEN TRADING</button>
                    <button type="button" onClick={() => setActiveView("strategies")}>VIEW STRATEGIES</button>
                    <button type="button" onClick={() => setActiveView("research")}>ENTER RESEARCH</button>
                  </div>
                </div>
                <div className="console-hub-status">
                  <span>SYSTEM STATE</span>
                  <strong className={`tone-${statusTone(overallStatus)}`}>{String(overallStatus).toUpperCase()}</strong>
                  <small>{wsConnected ? "Live socket connected" : "Using latest API snapshot"}</small>
                </div>
              </div>

              <div className="console-hub-metrics">
                <Metric
                  label="TODAY PNL"
                  value={money(todayPnl, { signed: true })}
                  detail={todayPnl === null ? "Not reported" : "Trading summary"}
                  tone={todayPnl > 0 ? "good" : todayPnl < 0 ? "bad" : "neutral"}
                />
                <Metric
                  label="OPEN BOOK"
                  value={activePositionCount ?? EMPTY}
                  detail={positionsReady ? `${positionStats.longs} long · ${positionStats.shorts} short` : "Loading positions"}
                />
                <Metric
                  label="PORTFOLIO HEAT"
                  value={percent(portfolioHeatRatio, { ratio: true })}
                  detail={accountRows.length ? `${accountRows.length} reported accounts` : "Not reported"}
                  tone={portfolioHeatRatio === null ? "muted" : portfolioHeatRatio > 0.65 ? "bad" : portfolioHeatRatio > 0.4 ? "warn" : "good"}
                />
                <Metric
                  label="ENGINE RECORDS"
                  value={engineRows.length || EMPTY}
                  detail={governanceRows.length ? `${governanceRows.length} governance records` : "Governance unavailable"}
                />
              </div>

              <div className="console-hub-modules">
                <button type="button" onClick={() => setActiveView("trading")}>
                  <span>MARKET & TRADING</span>
                  <strong>{currentBtc === null ? EMPTY : money(currentBtc)}</strong>
                  <small>{activePositionCount ?? EMPTY} open positions · {positionStats.longs}L / {positionStats.shorts}S</small>
                </button>
                <button type="button" onClick={() => setActiveView("strategies")}>
                  <span>ENGINE LOAD</span>
                  <strong>{engineRows.length || EMPTY}</strong>
                  <small>{governanceRows.length ? `${governanceRows.length} governance records` : "Governance not reported"}</small>
                </button>
                <button type="button" onClick={() => setActiveView("trading")}>
                  <span>EXPOSURE & ACCOUNTS</span>
                  <strong>{percent(portfolioHeatRatio, { ratio: true })}</strong>
                  <small>{accountRows.length ? `${accountRows.length} reported accounts` : "Account detail not reported"}</small>
                </button>
                <button type="button" onClick={() => setActiveView("outlook")}>
                  <span>ORACLE STATE</span>
                  <strong>{stats?.regime || stats?.market_regime || EMPTY}</strong>
                  <small>
                    H {compactNumber(firstNumber(stats?.H, stats?.hurst))} · C {compactNumber(firstNumber(stats?.C, stats?.coherence))} · S {compactNumber(firstNumber(stats?.S, stats?.entropy))}
                  </small>
                </button>
              </div>

              <div className="console-hub-grid">
                <div className="console-hub-panel">
                  <SectionTitle detail="LATEST CURRENT MESSAGES">AEON DISPATCH</SectionTitle>
                  {messageRows.length ? messageRows.slice(0, 5).map((message, index) => (
                    <article className="console-hub-message" key={message.id || `${message.timestamp || index}`}>
                      <div>
                        <strong>{message.context || message.type || "AEON"}</strong>
                        <time>{message.timestamp || message.created_at || EMPTY}</time>
                      </div>
                      <p>{message.message || message.text || "Message payload contained no display text."}</p>
                    </article>
                  )) : <EmptyPanel title="NO CURRENT DISPATCH" detail="The bot message endpoint returned no items." />}
                </div>

                <div className="console-hub-panel">
                  <SectionTitle detail="REPORTED BACKEND HEALTH">SYSTEM PULSE</SectionTitle>
                  <div className="console-hub-services">
                    {serviceRows.length ? serviceRows.slice(0, 10).map((service, index) => {
                      const state = service.status || service.state || "unknown";
                      return (
                        <div key={`${service.name || index}`}>
                          <span>{service.name || service.service || `service ${index + 1}`}</span>
                          <strong className={`tone-${statusTone(state)}`}>{String(state).toUpperCase()}</strong>
                        </div>
                      );
                    }) : <EmptyPanel title="NO SERVICE DETAIL" detail="System health did not include a services collection." />}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {activeView === "trading" ? (
            <>
              <div className="console-metrics">
                <Metric
                  label="UNREALIZED PNL"
                  value={positionsReady ? money(positionStats.unrealized, { signed: true }) : EMPTY}
                  detail={positionsReady ? `${positions.length} positions · ${money(positionStats.notional)} exposure` : "Loading paper positions"}
                  tone={positionStats.unrealized > 0 ? "good" : positionStats.unrealized < 0 ? "bad" : "neutral"}
                />
                <Metric
                  label="TODAY PNL"
                  value={money(todayPnl, { signed: true })}
                  detail={todayPnl === null ? "Backend did not provide this metric" : "Trading summary"}
                  tone={todayPnl > 0 ? "good" : todayPnl < 0 ? "bad" : "neutral"}
                />
                <Metric
                  label="WIN RATE"
                  value={percent(winRate)}
                  detail={totalTrades === null ? "Trade count unavailable" : `${compactNumber(totalTrades)} recorded trades`}
                  tone={winRate === null ? "muted" : winRate >= 50 ? "good" : "warn"}
                />
                <Metric
                  label="PORTFOLIO HEAT"
                  value={percent(portfolioHeatRatio, { ratio: true })}
                  detail={accountRows.length ? `${accountRows.length} active accounts` : "Account detail unavailable"}
                  tone={portfolioHeatRatio === null ? "muted" : portfolioHeatRatio > 0.65 ? "bad" : portfolioHeatRatio > 0.4 ? "warn" : "good"}
                />
                <Metric
                  label="BTC MARK"
                  value={currentBtc === null ? EMPTY : money(currentBtc)}
                  detail={wsConnected ? "Live transport connected" : "Last API snapshot"}
                />
              </div>

              <section className="console-book">
                <SectionTitle
                  detail={`${activePositionCount ?? EMPTY} OPEN · ${positionStats.longs}L / ${positionStats.shorts}S`}
                  action={<span className="console-readonly">POSITION ACTIONS OFF IN PREVIEW</span>}
                >
                  THE BOOK
                </SectionTitle>

                <div className="console-book-head">
                  <span>SYMBOL</span><span>SIDE</span><span>ACCOUNT</span><span>ENTRY</span>
                  <span>MARK</span><span>UNREALIZED</span><span>SIZE</span><span>ENGINE</span>
                </div>

                {!positionsReady ? (
                  <EmptyPanel title="LOADING POSITIONS" detail="Waiting for /api/paper/positions." />
                ) : positionsError ? (
                  <EmptyPanel title="POSITIONS UNAVAILABLE" detail={positionsError} />
                ) : positions.length === 0 ? (
                  <EmptyPanel title="NO OPEN POSITIONS" detail="The paper positions endpoint returned an empty book." />
                ) : positions.map((position, index) => {
                  const pnl = positionPnl(position);
                  const isSelected = selectedPosition === index;
                  return (
                    <React.Fragment key={`${position.account_id || position.account || "account"}-${position.symbol || index}`}>
                      <button
                        type="button"
                        className={`console-book-row ${isSelected ? "is-selected" : ""}`}
                        onClick={() => setSelectedPosition(isSelected ? null : index)}
                      >
                        <strong>{cleanSymbol(position.symbol || position.pair)}</strong>
                        <span className={`tone-${getPositionSide(position).includes("LONG") ? "good" : "bad"}`}>{getPositionSide(position)}</span>
                        <span>{position.account_id || position.account || EMPTY}</span>
                        <span>{compactNumber(positionEntry(position))}</span>
                        <span>{compactNumber(positionMark(position))}</span>
                        <span className={`tone-${pnl > 0 ? "good" : pnl < 0 ? "bad" : "neutral"}`}>{money(pnl, { signed: true })}</span>
                        <span>{compactNumber(positionSize(position))}</span>
                        <span>{String(positionEngine(position)).replaceAll("_", " ")}</span>
                      </button>
                      {isSelected ? (
                        <div className="console-position-detail">
                          <span><small>STOP</small>{compactNumber(firstNumber(position.stop_loss, position.sl))}</span>
                          <span><small>TARGET</small>{compactNumber(firstNumber(position.take_profit, position.tp))}</span>
                          <span><small>LEVERAGE</small>{firstNumber(position.leverage) === null ? EMPTY : `${firstNumber(position.leverage)}x`}</span>
                          <span><small>OPENED</small>{position.opened_at || position.created_at || EMPTY}</span>
                          <span className="console-readonly">Displayed from backend response · no synthetic values</span>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </section>

              <section className="console-market">
                <SectionTitle detail="LATEST MARKET SNAPSHOT FROM CURRENT AEON API">MARKET BOARD</SectionTitle>
                {marketRows.length ? (
                  <div className="console-market-grid">
                    {marketRows.map((row, index) => {
                      const change = marketChange(row);
                      return (
                        <div key={`${row.symbol || index}`}>
                          <strong>{cleanSymbol(row.symbol)}</strong>
                          <span>{compactNumber(row.price)}</span>
                          <small className={`tone-${change > 0 ? "good" : change < 0 ? "bad" : "muted"}`}>
                            {percent(change, { signed: true })}
                          </small>
                        </div>
                      );
                    })}
                  </div>
                ) : <EmptyPanel title="MARKET DATA UNAVAILABLE" detail="No symbols were returned by the current market endpoint." />}
              </section>
            </>
          ) : null}

          {activeView === "strategies" ? (
            <section className="console-full-section">
              <SectionTitle detail="CURRENT ENGINE STATUS + GOVERNANCE">STRATEGY ENGINES</SectionTitle>
              <div className="console-table console-engine-table">
                <div className="console-table-head"><span>ENGINE</span><span>RUNTIME</span><span>OPEN</span><span>GOVERNANCE</span><span>TIER</span><span>SCORE</span></div>
                {engineRows.length ? engineRows.map((engine, index) => {
                  const key = String(engine.name || engine.engine || engine.id || "");
                  const gov = governanceRows.find((row) =>
                    String(row.engine || row.name || "").toLowerCase().includes(key.split("_")[0].toLowerCase())
                  );
                  const runtime = engine.status || engine.state || (engine.config?.active === false ? "disabled" : "active");
                  return (
                    <div className="console-table-row" key={`${key}-${index}`}>
                      <strong>{key.replaceAll("_", " ") || EMPTY}</strong>
                      <span className={`tone-${statusTone(runtime)}`}>{String(runtime).toUpperCase()}</span>
                      <span>{firstNumber(engine.open_count, engine.positions) ?? EMPTY}</span>
                      <span className={`tone-${statusTone(gov?.current_recommendation || gov?.recommendation)}`}>{gov?.current_recommendation || gov?.recommendation || EMPTY}</span>
                      <span>{gov?.current_tier || gov?.tier || EMPTY}</span>
                      <span>{compactNumber(firstNumber(gov?.current_score, gov?.score, gov?.edge_score))}</span>
                    </div>
                  );
                }) : <EmptyPanel title="ENGINE STATUS UNAVAILABLE" detail="The current engine status endpoint did not return a usable list." />}
              </div>
            </section>
          ) : null}

          {activeView === "outlook-summary" ? (
            <section className="console-full-section">
              <SectionTitle detail="ONLY VALUES REPORTED BY CURRENT AEON">ORACLE READ</SectionTitle>
              <div className="console-oracle-grid">
                {[
                  ["REGIME", stats?.regime || stats?.market_regime],
                  ["HURST / H", firstNumber(stats?.H, stats?.hurst)],
                  ["COHERENCE / C", firstNumber(stats?.C, stats?.coherence)],
                  ["ENTROPY / S", firstNumber(stats?.S, stats?.entropy)],
                  ["FW STATUS", fwSummary?.status],
                  ["FW TIER", fwSummary?.health_7d?.tier],
                  ["FW SCORE", firstNumber(fwSummary?.health_7d?.score)],
                  ["RESEARCHER", researcherData?.status],
                ].map(([label, value]) => (
                  <div key={label}>
                    <span>{label}</span>
                    <strong><DataState available={value !== null && value !== undefined && value !== ""}>{String(value ?? EMPTY)}</DataState></strong>
                  </div>
                ))}
              </div>
              <div className="console-note">
                <strong>IMPLEMENTATION NOTE</strong>
                <p>The Cloud concept included confidence, intent, and prediction values that are not guaranteed by the current backend. This preview leaves them blank until an authoritative endpoint exists.</p>
              </div>
            </section>
          ) : null}

          {activeView === "desk-summary" ? (
            <section className="console-full-section">
              <SectionTitle detail="CURRENT BOT MESSAGE STREAM + RESEARCH COMPONENTS">RESEARCH TEAM</SectionTitle>
              <div className="console-intel-layout">
                <div className="console-feed">
                  {messageRows.length ? messageRows.map((message, index) => (
                    <article key={message.id || `${message.timestamp || index}`}>
                      <div>
                        <strong>{message.context || message.type || "AEON"}</strong>
                        <time>{message.timestamp || message.created_at || EMPTY}</time>
                      </div>
                      <p>{message.message || message.text || "Message payload contained no display text."}</p>
                    </article>
                  )) : <EmptyPanel title="NO MESSAGE STREAM" detail="The current bot messages endpoint returned no items." />}
                </div>
                <div className="console-services">
                  <strong>RESEARCH COMPONENTS</strong>
                  {researcherData?.components && Object.keys(researcherData.components).length ? Object.entries(researcherData.components).map(([name, component]) => {
                    const state = component?.status || component?.state || component || "unknown";
                    return (
                      <div key={name}>
                        <span>{name.replaceAll("_", " ")}</span>
                        <small className={`tone-${statusTone(state)}`}>{String(state).toUpperCase()}</small>
                      </div>
                    );
                  }) : <EmptyPanel title="NO COMPONENT DETAIL" detail="The researcher endpoint did not report component states." />}
                  {operatorWarnings?.length ? (
                    <div className="console-warning-list">
                      <strong>OPERATOR WARNINGS</strong>
                      {operatorWarnings.map((warning) => <p key={warning}>{warning}</p>)}
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : null}

          {activeView === "research-summary" ? (
            <section className="console-full-section">
              <SectionTitle detail="CURRENT RESEARCHER OUTPUT · NO PLACEHOLDER RESULTS">THE LAB</SectionTitle>
              <div className="console-lab-summary">
                <Metric label="RESEARCHER" value={String(researcherData?.status || EMPTY).toUpperCase()} detail="Current researcher status" tone={statusTone(researcherData?.status)} />
                <Metric label="HURST SYMBOLS" value={asArray(researcherData?.hurst?.symbols).length || EMPTY} detail="Reported classifications" />
                <Metric label="ELEVATED CONSENSUS" value={firstNumber(researcherData?.ensemble?.elevated_count) ?? EMPTY} detail="Current ensemble output" />
                <Metric label="CALIBRATION TRADES" value={firstNumber(researcherData?.calibration?.total_trades) ?? EMPTY} detail="Backend sample size" />
              </div>

              <div className="console-lab-grid">
                <div className="console-lab-panel">
                  <SectionTitle detail="REPORTED SYMBOL CLASSIFICATIONS">HURST REGIMES</SectionTitle>
                  {asArray(researcherData?.hurst?.symbols).length ? (
                    <div className="console-lab-list">
                      {researcherData.hurst.symbols.map((row, index) => (
                        <div key={`${row.symbol || index}`}>
                          <strong>{cleanSymbol(row.symbol)}</strong>
                          <span>{row.classification || row.regime || EMPTY}</span>
                          <small>{compactNumber(firstNumber(row.hurst, row.H, row.value))}</small>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyPanel title="NO HURST OUTPUT" detail="No symbol classifications were returned." />}
                </div>

                <div className="console-lab-panel">
                  <SectionTitle detail="CURRENT MODEL AGREEMENT">ENSEMBLE</SectionTitle>
                  {asArray(researcherData?.ensemble?.consensus).length ? (
                    <div className="console-lab-list">
                      {researcherData.ensemble.consensus.map((row, index) => (
                        <div key={`${row.symbol || index}`}>
                          <strong>{cleanSymbol(row.symbol)}</strong>
                          <span>{row.consensus || row.vote || row.signal || EMPTY}</span>
                          <small>{percent(firstNumber(row.confidence, row.score), { ratio: firstNumber(row.confidence, row.score) <= 1 })}</small>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyPanel title="NO ENSEMBLE OUTPUT" detail="No consensus records were returned." />}
                </div>

                <div className="console-lab-panel">
                  <SectionTitle detail="CURRENT NEURAL VOTE">TCN</SectionTitle>
                  <div className="console-lab-readout">
                    <span>VOTE</span>
                    <strong>{researcherData?.tcn?.vote || EMPTY}</strong>
                    <span>CONFIDENCE</span>
                    <strong>{percent(firstNumber(researcherData?.tcn?.confidence), { ratio: firstNumber(researcherData?.tcn?.confidence) <= 1 })}</strong>
                    <span>LIVE</span>
                    <strong>{researcherData?.tcn?.live === true ? "YES" : researcherData?.tcn?.live === false ? "NO" : EMPTY}</strong>
                  </div>
                </div>

                <div className="console-lab-panel">
                  <SectionTitle detail="CURRENT STRATEGY RECORDS">EXPERIMENTS</SectionTitle>
                  {asArray(researcherData?.strategies?.strategies).length ? (
                    <div className="console-lab-list">
                      {researcherData.strategies.strategies.map((row, index) => (
                        <div key={`${row.name || row.strategy || index}`}>
                          <strong>{row.name || row.strategy || `experiment ${index + 1}`}</strong>
                          <span>{row.status || row.state || EMPTY}</span>
                          <small>{compactNumber(firstNumber(row.score, row.sharpe, row.win_rate))}</small>
                        </div>
                      ))}
                    </div>
                  ) : <EmptyPanel title="NO EXPERIMENT RECORDS" detail="No strategy experiments were returned." />}
                </div>
              </div>
            </section>
          ) : null}

          {activeView === "room" ? (
            <section className="console-native-workspace">
              <SectionTitle detail="SHARED MARKET INTELLIGENCE · LIVE IMPLEMENTATION">MARKET ROOM</SectionTitle>
              <div className="console-native-body">
                <MarketRoomPage marketData={marketData} wsConnected={wsConnected} />
              </div>
            </section>
          ) : null}

          {activeView === "desk" ? (
            <section className="console-native-workspace">
              <SectionTitle detail="SPECIALISTS · COUNCIL · RECOMMENDATIONS · CHAT">INTELLIGENCE DESK</SectionTitle>
              <div className="console-native-body">
                <TeamPage />
              </div>
            </section>
          ) : null}

          {activeView === "brain" ? (
            <section className="console-native-workspace console-native-brain">
              <SectionTitle detail="CONVERSATION · PERFORMANCE · SIGNAL REVIEW">AEON BRAIN</SectionTitle>
              <div className="console-native-body">
                <MindPage />
              </div>
            </section>
          ) : null}

          {activeView === "outlook" ? (
            <section className="console-native-workspace">
              <SectionTitle detail="SEARCH · SCAN · ENTROPY · CALIBRATION">OUTLOOK / ORACLE</SectionTitle>
              <div className="console-native-body">
                <OracleDeck />
              </div>
            </section>
          ) : null}

          {activeView === "research" ? (
            <section className="console-native-workspace">
              <SectionTitle detail="NEXUS · ORIA · OMEGA · LAB · RESEARCH DESK">RESEARCH</SectionTitle>
              <div className="console-native-body">
                <ResearchSection />
              </div>
            </section>
          ) : null}
        </main>

        <aside className="console-spine">
          <div>
            <span>DATE</span>
            <strong>{displayDate}</strong>
          </div>
          <div>
            <span>SYSTEM</span>
            <strong className={`tone-${statusTone(overallStatus)}`}>{String(overallStatus).toUpperCase()}</strong>
          </div>
          <div>
            <span>BOOK</span>
            <strong>{activePositionCount ?? EMPTY}</strong>
          </div>
          <div>
            <span>DATA</span>
            <strong>{marketRows.length ? `${marketRows.length} MARKETS` : "NO MARKET FEED"}</strong>
          </div>
          <div className="console-spine-note">
            <span>PREVIEW RULE</span>
            <p>Unknown values remain blank. Trade-changing controls are intentionally disabled.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
